import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@powerful/ui';
import { isSocialEnabled, type SocialEnvSource, type SocialProvider } from '../../lib/auth/providers';

/**
 * Entrada social — **visível e desativada** enquanto a flag estiver desligada.
 *
 * Esconder os botões pareceria mais limpo e seria pior: quem criou conta na expectativa de entrar
 * com Google procuraria o botão, não o encontraria, e concluiria que a aplicação está partida. O
 * botão visível com estado desativado e explicação diz a verdade — a funcionalidade existe e ainda
 * não está aberta.
 *
 * 🔴 Com a flag desligada, nenhum SDK é chamado. A T1.13 **não liga** o social: OAuth de conta nova
 * não passa pela trava de idade nem pelo aceite jurídico (§2.3 do plano), logo a ativação é do M7.
 */
export interface SocialButtonsProps {
  env?: SocialEnvSource;
  /** Só é chamado quando a flag está ligada. */
  onSelect?: (provider: SocialProvider) => void;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 8.6h4.6a4.6 4.6 0 11-1.5-3.1" />
    <path d="M12 12h4.6" />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15.6 12.4c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.9-1.5-.1-2.9.9-3.6.9-.7 0-1.9-.9-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.5 3 2.4 1.2 0 1.6-.8 3.1-.8 1.4 0 1.8.8 3.1.7 1.3 0 2.1-1.2 2.9-2.3.6-.9.9-1.7 1-1.8-.1 0-2.3-.9-2.3-3.4z" />
    <path d="M13.4 5.2c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.8 1.5-.6.7-1.2 1.9-1 3 1 .1 2.1-.6 2.8-1.5z" />
  </svg>
);

// `ImportMetaEnv` é gerado pelo Vite e não declara a nossa flag; o cast estreita para o contrato
// lido por `isSocialEnabled`, que trata ausência e valor inesperado como "desligado".
const RUNTIME_ENV = import.meta.env as unknown as SocialEnvSource;

export function SocialButtons({ env = RUNTIME_ENV, onSelect }: SocialButtonsProps) {
  const { t } = useTranslation();
  const enabled = isSocialEnabled(env);
  const noticeId = useId();

  const providers: { id: SocialProvider; icon: ReactNode }[] = [
    { id: 'google', icon: <GoogleIcon /> },
    { id: 'apple', icon: <AppleIcon /> },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 text-[length:var(--t-xs)] text-ink-3 before:h-px before:flex-1 before:bg-[color:var(--hairline-2)] after:h-px after:flex-1 after:bg-[color:var(--hairline-2)]">
        {t('auth.social.or')}
      </div>

      {providers.map(({ id, icon }) => (
        <Button
          key={id}
          variant="ghost"
          full
          disabled={!enabled}
          aria-describedby={enabled ? undefined : noticeId}
          onClick={enabled ? () => onSelect?.(id) : undefined}
          data-social-provider={id}
        >
          {icon}
          <span>{t(`auth.social.${id}`)}</span>
          {enabled ? null : <span className="badge badge-warn">{t('auth.social.soon')}</span>}
        </Button>
      ))}

      {enabled ? null : (
        <p id={noticeId} className="text-center text-[length:var(--t-xs)] text-ink-3">
          {t('auth.social.disabledNotice')}
        </p>
      )}
    </div>
  );
}
